import { useState, useEffect, useMemo } from 'react'
import { createDefaultLayoutsForProduct } from '@/api/admin'
import { getAllProducts } from '@/api/products'
import { getThemeCategories } from '@/api/themes'
import { useSetToast } from '@/hooks'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Dropdown, FullscreenModal } from '@/components/global'
import { Button, Input } from '@/components/priority'

type ShowState = [boolean, (value: boolean) => void]

type Application = 'MDL' | 'M3'

type Theme = {
  id: string
  name: string
  application?: Application | null
}

/** A product as the products endpoint tags it: `source` says which table it lives in. */
type Product = {
  id: string
  name: string
  archived?: boolean
  source?: 'mdl' | 'm3'
}

/** Dropdown value meaning "every active theme of the selected application". */
const ALL_THEMES_ID = 'all'

/** A page count is a positive whole number; no decimals, signs, or exponents. */
const WHOLE_NUMBER_PATTERN = /^[1-9]\d*$/

type CreateDefaultLayoutsForProductModalProps = {
  show: ShowState
}

/**
 * Admin action that seeds a default layout (GlobalTemplate flagged default +
 * blank pages) for one product on one theme, or on every active theme of its
 * application, wherever the product has none — the same layouts a freshly
 * created product gets. Used when a product was created but its layouts
 * never seeded, or a theme was added after the product.
 */
export const CreateDefaultLayoutsForProductModal = ({
  show,
}: CreateDefaultLayoutsForProductModalProps) => {
  const close = show[1]
  const [products, setProducts] = useState<Product[]>([])
  const [themes, setThemes] = useState<Theme[]>([])
  const [selectedApp, setSelectedApp] = useState<Application>('MDL')
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedTheme, setSelectedTheme] = useState(ALL_THEMES_ID)
  // Kept as typed text so "2.5" is rejected instead of truncated to 2.
  const [numberOfPages, setNumberOfPages] = useState('')
  const [loading, setLoading] = useState(false)
  const setToast = useSetToast()

  const resetState = () => {
    setSelectedApp('MDL')
    setSelectedProduct('')
    setSelectedTheme(ALL_THEMES_ID)
    setNumberOfPages('')
    close(false)
  }

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Without a product/order filter the themes endpoint returns themes
        // for both applications; the products endpoint always returns both
        // tables tagged by source. The app toggle filters both client-side.
        const [productResponse, themeResponse] = await Promise.all([
          getAllProducts(),
          getThemeCategories(),
        ])
        setProducts(
          (productResponse.data as Product[])
            .filter((product) => !product.archived)
            .sort((a, b) => a.name.localeCompare(b.name)),
        )
        setThemes(
          (themeResponse.data[0].themes as Theme[]).sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        )
      } catch (error) {
        console.error('Failed to fetch products and themes:', error)
      }
    }

    fetchOptions()
  }, [])

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (product) => (product.source === 'm3' ? 'M3' : 'MDL') === selectedApp,
      ),
    [products, selectedApp],
  )

  // Themes are flagged MDL when application is missing, matching how the
  // backend defaults themes.application.
  const themeOptions = useMemo(
    () => [
      { id: ALL_THEMES_ID, name: 'All themes' },
      ...themes.filter(
        (theme) => (theme.application === 'M3' ? 'M3' : 'MDL') === selectedApp,
      ),
    ],
    [themes, selectedApp],
  )

  const handleAppChange = (app: Application) => {
    setSelectedApp(app)
    setSelectedProduct('')
    setSelectedTheme(ALL_THEMES_ID)
  }

  const canSubmit = !loading && Boolean(selectedProduct)

  const handleSubmit = async () => {
    if (!canSubmit) return

    // Blank means "copy from the product's existing layout"; the server
    // refuses the request when it has none. Anything else must be a whole
    // number of pages.
    const trimmedPages = numberOfPages.trim()
    if (trimmedPages && !WHOLE_NUMBER_PATTERN.test(trimmedPages)) {
      setToast({
        text: 'Number of pages must be a whole number of 1 or more.',
        icon: 'Error',
      })
      return
    }

    setLoading(true)
    try {
      await createDefaultLayoutsForProduct({
        productId: selectedProduct,
        application: selectedApp,
        themeId: selectedTheme === ALL_THEMES_ID ? undefined : selectedTheme,
        numberOfPages: trimmedPages ? Number(trimmedPages) : undefined,
      })
      setToast({
        text: 'Default layout creation queued! Layouts will appear shortly.',
        icon: 'Check',
      })
      resetState()
    } catch (error) {
      console.error('Failed to create default layouts for product:', error)
      // The route answers a request it can never seed (no page count for a
      // never-laid-out product, no paper size) with the reason.
      const serverMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message
      setToast({
        text:
          serverMessage ??
          'Failed to create default layouts. Please try again.',
        icon: 'Error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    // @ts-expect-error N/A
    <FullscreenModal show={show} hideClose>
      <Flex
        column
        padding="32px"
        width="500px"
        height="700px"
        justify="space-between"
      >
        <Flex column>
          <Text size="24px" weight="600" margin="0 0 20px 0">
            Create Default Layouts for Product
          </Text>
          <Text margin="0 0 20px 0">
            Checks the selected theme, or every active theme of the application,
            for the product's default layout and creates a blank one wherever it
            is missing, the same way a newly created product gets its layouts.
            Page count is copied from the product's existing default layout;
            enter one below only for a product that has never been laid out.
            Existing layouts are never overwritten.
          </Text>
          <Flex margin="0 0 24px 0">
            <Button
              primary={selectedApp === 'MDL'}
              margin="0 8px 0 0"
              onClick={() => handleAppChange('MDL')}
            >
              MDL
            </Button>
            <Button
              primary={selectedApp === 'M3'}
              onClick={() => handleAppChange('M3')}
            >
              M3
            </Button>
          </Flex>
          <Dropdown
            width="100%"
            margin="0 0 24px 0"
            color="inherit"
            // @ts-expect-error N/A
            selected={[selectedProduct, setSelectedProduct]}
            placeholder={`Select a ${selectedApp} product`}
            items={filteredProducts}
            valueKey="id"
            textKey="name"
            full={true}
          />
          <Dropdown
            width="100%"
            margin="0 0 24px 0"
            color="inherit"
            // @ts-expect-error N/A
            selected={[selectedTheme, setSelectedTheme]}
            placeholder="Select a theme"
            items={themeOptions}
            valueKey="id"
            textKey="name"
            full={true}
          />
          <Flex column width="100%" margin="0 0 24px 0">
            <Input
              // @ts-expect-error N/A
              width="100%"
              inputMode="numeric"
              label="Number of pages (only if the product has no layout yet)"
              value={[numberOfPages, setNumberOfPages]}
              placeholder="Copied from the existing layout when blank"
            />
          </Flex>
        </Flex>
        <Flex justify="flex-end" margin="24px 0 0 0">
          <Button margin="0 16px 0 0" onClick={resetState}>
            Cancel
          </Button>
          <Button
            margin="0 12px 0 0"
            primary
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {loading ? 'Queueing...' : 'Create Default Layouts'}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}

export default CreateDefaultLayoutsForProductModal
